import { RSB_URL } from 'helpers/common/rsb';

const truckOpenLabels = [
    "Let's Serve Up Success!",
    "Let's Fire Up the Flavor!",
    'Time to Taste Triumph!',
    'Unleash the Feast!',
    'Time to Spice Up Success!',
];

const truckOpenTexts = [
    'The customers are hungry!',
    'Cooking up a storm.',
    'The butter is sizzling.',
    "Time fries when you're having fun.",
    'Marinating the results.',
];

export const lessons: LessonsSetup = {
    '1.1': {
        title: 'Start Your Business',
        text: `<p>As an ambitious and innovative chef, you want to start an international chain of top tier restaurants. But you're going to start with a Minimum Viable Product - in this case - a food truck!</p>
                <p>Let's start by naming your business.</p>
                <p><b>Type your name directly into the sign on the truck.</b></p>`,
        button1: {
            label: ({ customized }) => (customized ? `Let's go with '${customized}'` : "Can't you pick a name for me?"),
            lesson: '1.2',
        },
    },
    '1.2': {
        title: 'Start Your Business',
        text: `<p>"{truckName}" it is!</p>
                <p>What type of food are you going to sell?</p>
                <p><b>Select your product.</b></p>`,
    },
    '1.3': {
        title: 'Start Your Business',
        text: `<p>Great! You've got a vision. You want to start "{truckName}" selling {foodType}.</p>
                <p>Unfortunately, you are broke.</p>
                <p>To start this business, you'll need some cash to pay the lease on the truck, purchase cooking supplies, and put gas in your tank.</p>
                <p>Let's head to your local credit union for a loan.</p>`,
        button1: {
            label: 'Show me the money!',
            lesson: '1.4',
        },
    },
    '1.4': {
        image: '/assets/illustrations/banker-meeting.png',
        imageAlt: 'Banker from the waist up',
        title: 'Make Your Pitch',
        text: `<p>Greetings, I'm the Bank Officer!</p>
                <p>Food trucks are a risky business. I can't give you the money until I know when you'll pay us back. I need a <u>Prediction</u>.</p>
                <p>Food trucks selling {foodType} typically make anywhere from \${predictionMin} a month to \${predictionMax}.</p>
                <p>How much money will you make per month?</p>
                <p><b>Use the slider to update your prediction.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'Tons....just tons of money.',
            lesson: '1.4.1',
        },
        button2: {
            label: 'Exactly ${predictionInitialMonthly}',
            lesson: '1.5',
        },
        button3: {
            color: 'secondary',
            label: 'I have no idea',
            lesson: '1.4.2',
        },
    },
    '1.4.1': {
        image: '/assets/illustrations/banker-scratching-head.png',
        imageAlt: 'Banker scratching his head',
        title: 'Umm....',
        text: `<p>Ok, I respect your enthusiasm but that's not good enough to take out a loan.</p>
                <p>Food trucks selling {foodType} typically make anywhere from \${predictionMin} a month to \${predictionMax}.</p>
                <p><b>How much money will you make per month?</b></p>`,
        button1: {
            label: 'Exactly ${predictionInitialMonthly}',
            lesson: '1.5',
        },
        button2: {
            color: 'secondary',
            label: 'I have no idea',
            lesson: '1.4.2',
        },
    },
    '1.4.2': {
        image: '/assets/illustrations/banker-shrugging.png',
        imageAlt: 'Banker shrugging',
        title: 'Respect',
        text: `<p>Ok, I respect your honesty and we're a friendly community bank. We'll give you the benefit of the doubt and help you along the way.</p>
                <p>So I still need a Prediction.</p>
                <p>Food trucks selling {foodType} typically make anywhere from \${predictionMin} a month to \${predictionMax}.</p>
                <p><b>How much money will you make per month?</b></p>`,
        button1: {
            color: 'secondary',
            label: 'Tons....just tons of money.',
            lesson: '1.4.1',
        },
        button2: {
            label: 'Exactly ${predictionInitialMonthly}',
            lesson: '1.5',
        },
    },
    '1.5': {
        image: '/assets/illustrations/banker-handing-over-money.png',
        imageAlt: 'Banker handing over money',
        title: 'Loan Approved',
        text: `<p>Sounds good. You're committing to make \${predictionInitialMonthly} per month. Based on that, I can approve just enough funding to get you through your first month.</p>
            <p>You'll need to prove your prediction is accurate to get funding for a full restaurant. Come back in one month and we'll see how you did.</p>`,
        button1: {
            labels: truckOpenLabels,
            lesson: '1.6',
        },
    },
    '1.6': {
        texts: truckOpenTexts,
    },
    '1.7': {
        image: '/assets/illustrations/banker-pointing-to-chart.png',
        imageAlt: 'Banker pointing to chart',
        title: 'Not Quite!',
        text: `<p>Your prediction is off by \${dailyOffset} per day, but that's a \${yearlyOffset} difference per year.</p>
                <p>If you can't make good predictions, then <i>we can't fund your food truck</i>.</p>`,
        button1: {
            color: 'secondary',
            label: "Yes, I know exactly how much we'll earn.",
            lesson: '1.7.1',
        },
        button2: {
            label: 'Yes, but I think I need some help to make a financial model.',
            lesson: '1.x',
        },
        button3: {
            color: 'secondary',
            label: "No, forget you. I'm going to a different bank.",
            lesson: '1.7.2',
        },
    },
    '1.7.1': {
        image: '/assets/illustrations/banker-meeting.png',
        imageAlt: 'Banker from the waist up',
        title: 'Second Chance',
        text: `<p>Ok. You've learned from your last month in business and now can predict with 100% certainty your monthly revenue. Let's hear your new Prediction.</p>
                <p><b>How much money will you make per month?</b></p>`,
        button1: {
            color: 'secondary',
            label: 'Exactly ${predictionInitialMonthly}',
            lesson: '1.5',
        },
        button3: {
            label: 'Is this how investors in the real world work?',
            lesson: '1.x',
        },
    },
    '1.7.2': {
        image: '/assets/illustrations/banker-meeting.png',
        imageAlt: 'Banker from the waist up',
        title: 'Make Your Pitch',
        text: `<p>Greetings, I'm the Bank Officer from the substantially less friendly bank down the street from the community bank.</p>
            <p>Food trucks are a risky business. I can't give you the money until I know when you'll pay us back. I need a <u>Prediction</u>.</p>
            <p>Food trucks selling {foodType} typically make anywhere from $2,000 a month to $20,000.</p>
            <p><b>How much money will you make per month?</b></p>`,
        button1: {
            color: 'secondary',
            label: 'Exactly ${predictionInitialMonthly}',
            lesson: '1.5',
        },
        button2: {
            label: 'Is this how investors in the real world work?',
            lesson: '1.x',
        },
    },
    '1.7.3': {
        image: '/assets/illustrations/banker-facepalm.png',
        imageAlt: 'Banker facepalm',
        title: 'Oops...',
        text: ({ worse }) => `<p>Well that was ${
            worse ? 'even worse' : 'not much better'
        }. Your prediction is off by \${dailyOffset} per day, but that's a \${yearlyOffset} difference per year.</p>
                <p>It's very hard to make perfect predictions with a risky new business...especially if it's innovative.</p>
                <p>We can only give you another loan if you work with one of our business coaches to come up with a more accurate prediction.</p>`,
        button1: {
            label: 'Is this how investors in the real world work?',
            lesson: '1.x',
        },
        button2: {
            color: 'secondary',
            label: "Forget you. I'm going to a different bank. ",
            lesson: '1.7.2',
        },
    },
    '1.x': {
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Globe',
        title: 'The Real World',
        text: `<p>Yes, in the real world, both banks and investors expect you to make predictions about your ROI or impact at some point.</p>
                <p>Sadly, most entrepreneurs (and corporate executives) wildly overestimate their potential outcomes by 70% or more! <u>Even if they are experts in their field!</u></p>
                <ul>
                    <li>Initial predictions are almost always wrong.</li>
                    <li>Investors will often tolerate early mistakes,</li>
                    <li>But only if they are fixed!</li>
                </ul>
                <p>Innovation Accounting methods can improve predictions.</p>`,
        button1: {
            label: 'Ok, how do I improve my prediction?',
            lesson: '2',
        },
    },
    '2': {
        heading: 'Level 2',
        title: 'Map Your Financial Model',
        text: 'Visualize your business as a funnel. Understand what drives success step by step.',
        button: 'Play',
        image: '/assets/illustrations/treasure-map.png',
        imageAlt: 'A treasure map',
    },
    '2.1': {
        image: '/assets/illustrations/coach-waving.png',
        imageAlt: 'Coach waving',
        title: "Hi, I'm Your Coach",
        text: `<p>The bank has asked me to help you turn your business assumptions into accurate <u>Predictions</u>.</p>
                <p>We'll do something called innovation accounting - which is much less boring than it sounds.</p>`,
        button1: {
            color: 'secondary',
            label: 'Assumptions? Does this have something to do with my Business Model Canvas?',
            lesson: '2.1.1',
        },
        button2: {
            label: 'Ok...but does this require math?',
            lesson: '2.2',
        },
    },
    '2.1.1': {
        image: '/assets/illustrations/coach-thinking.png',
        imageAlt: 'Coach thinking with hand on chin',
        title: 'Business Assumptions',
        text: `<p>Yes, exactly. The Business Model Canvas maps your assumptions about the business so you can test them.</p>
                <p>We'll do the same thing. But you're running a real business with real dollars and the bank needs a real financial model. So we're going to focus on the numbers instead of a bunch of sticky notes on a canvas.</p>
                <p>Ready for the math?</p>`,
        button1: {
            label: 'Do we have to do math?',
            lesson: '2.2',
        },
    },
    '2.2': {
        image: '/assets/illustrations/coach-no-math.png',
        imageAlt: 'Coach with no math symbol',
        title: 'Minimal Math',
        text: `<p>Don't worry - you won't have to do any actual math. I'll do the actual calculations - but we will talk about numbers.</p>
                <p>Right now, instead of trying to guess the revenue, let's make a step-by-step model of a single customer's journey</p>
                <p><b>Click the (+) button to add the first moment the customer sees your truck.</b></p>`,
    },
    '2.3': {
        image: '/assets/illustrations/person-walking-by-food-truck.png',
        imageAlt: 'Person walking by food truck',
        title: 'Make sure they pay',
        text: `<p>Great, people walk by and see your truck.</p>
                <p>Now let's make sure we add the final step of this journey. Each person should pay us.</p>
                <p><b>Click the (+) button to add the last step.</b></p>`,
    },
    '2.4': {
        image: '/assets/illustrations/person-paying.png',
        imageAlt: 'Person paying',
        title: 'Fill in the Middle',
        text: `<p>We have the beginning and end of their journey.</p>
                <p>Now add the missing steps in the middle so we have a complete picture of the customer journey.</p>
                <p><b>Click the (+) button to add the next step.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'I already know about customer journey maps. Please fill this in for me.',
            lesson: '2.8',
        },
    },
    '2.5': {
        image: '/assets/illustrations/person-hungry.png',
        imageAlt: 'Person hungry',
        title: 'Add the Third Step',
        text: `<p>Some people will walk by but only some of those people will be hungry.</p>
                <p><b>Click the (+) button to add the next step.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'I already know about customer journey maps. Please fill this in for me.',
            lesson: '2.8',
        },
    },
    '2.6': {
        image: '/assets/illustrations/person-reading-menu.png',
        imageAlt: 'Person reading menu',
        title: 'Add the Fourth Step',
        text: `<p>Some of those hungry people will be interested in {foodType} and will look at our menu.</p>
                <p><b>Click the (+) button to add the next step.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'I already know about customer journey maps. Please fill this in for me.',
            lesson: '2.8',
        },
    },
    '2.7': {
        image: '/assets/illustrations/coach-pointing.png',
        imageAlt: 'Coach excited pointing finger in the air',
        title: 'A Complete Journey',
        text: `<p>Great! You have a simple customer journey from walking by to placing an order.</p>
                <p>Not everyone will follow this journey, some people will drop off - so it's a funnel.</p>
                <p>Now we can estimate each step of the funnel separately. This makes estimating a bit easier and more accurate.</p>`,
        button1: {
            color: 'secondary',
            label: 'How does it make estimating easier?',
            lesson: '2.8',
        },
        button2: {
            label: "Let's make some estimates.",
            lesson: '2.9',
        },
    },
    '2.8': {
        image: '/assets/illustrations/coach-thinking.png',
        imageAlt: 'Coach thinking with hand on chin',
        title: 'Simpler Estimates',
        text: `<p>Breaking a complex estimation into smaller pieces can help because we either already know some of the smaller pieces or they are more observable.</p>
                <p>For example, instead of estimating the revenue, we can observe how many customers walk down the street each day.</p>
                <p>If you are curious, this process is called a <a href="https://en.wikipedia.org/wiki/Fermi_problem" target="_blank">Fermi decomposition</a>.</p>`,
        button1: {
            label: "Ok, Let's make some estimates.",
            lesson: '2.9',
        },
    },
    '2.9': {
        image: '/assets/illustrations/coach-crystal-ball.png',
        imageAlt: 'Coach with crystal ball',
        title: 'Estimate the First Step',
        text: `<p>Now let's look at numbers. It's easier to calculate the number of people who engage with your food truck each day than each month.</p>
                <p>So how many people do you think walk by your food truck in a day?</p>
                <p><b>Estimate the first step.</b></p>
                <p><u>Hint</u>: You can just use the placeholder number.</p>`,
        button1: {
            action: 'fill',
            color: 'secondary',
            label: 'I know how to make step by step quantitative estimates. Please fill this in for me.',
        },
    },
    '2.9.1': {
        image: '/assets/illustrations/coach-facepalming.png',
        imageAlt: 'Coach facepalming',
        title: 'Estimate the First Step',
        text: `<p>Ok that's a pretty {direction} estimate.</p>
                <p>From my experience, the foot traffic in this area is somewhere between {minValue} and {maxValue}.</p>
                <p><b>Please pick a number in that range.</b></p>`,
        button1: {
            action: 'fill',
            color: 'secondary',
            label: 'I know how to make step by step quantitative estimates. Please fill this in for me.',
        },
    },
    '2.10': {
        image: '/assets/illustrations/coach-crystal-ball.png',
        imageAlt: 'Coach with crystal ball',
        title: 'Estimate the 2nd Step',
        text: `<p>Great, but not everyone who walks by will be hungry.</p>
                <p>Each step has some friction in it that prevents people from making it all the way through the funnel.</p>
                <p>So let's create estimates for how people make it through each step.</p>
                <p><b>Estimate the second step.</b></p>
                <p><u>Hint</u>: You can just use the placeholder number.</p>`,
    },
    '2.10.1': {
        image: '/assets/illustrations/coach-facepalming.png',
        imageAlt: 'Coach facepalming',
        title: 'Estimate the 2nd Step',
        text: ({ overflow }) => `<p>That's a pretty {direction} estimate. ${
            overflow ? "You can't have more hungry people than people walking by." : ''
        }</p>
                                <p>From my experience and based on your prior estimate, the number of hungry people will be somewhere between {minValue} and {maxValue}.</p>
                                <p><b>Please pick a number in that range.</b></p>`,
    },
    '2.11': {
        image: '/assets/illustrations/coach-crystal-ball.png',
        imageAlt: 'Coach with crystal ball',
        title: 'Estimate the 3rd Step',
        text: `<p>Now how many people will look at the menu?</p>
                <p><b>Estimate the third step.</b></p>
                <p><u>Hint</u>: You can just use the placeholder number.</p>`,
    },
    '2.11.1': {
        image: '/assets/illustrations/coach-facepalming.png',
        imageAlt: 'Coach facepalming',
        title: 'Estimate the 3rd Step',
        text: ({ overflow }) => `<p>That's a pretty {direction} estimate. ${
            overflow
                ? "If someone isn't hungry, they won't look at the menu and place an order. So this must be lower than the number of hungry people."
                : ''
        }</p>
                                <p>From my experience and based on your prior estimates, the number people looking at the menu will be somewhere between {minValue} and {maxValue}.</p>
                                <p><b>Please pick a number in that range.</b></p>`,
    },
    '2.12': {
        image: '/assets/illustrations/coach-crystal-ball.png',
        imageAlt: 'Coach with crystal ball',
        title: 'Estimate the 4th Step',
        text: `<p>Now how many people will place an order?</p>
                <p><b>Estimate the fourth step.</b></p>
                <p><u>Hint</u>: You can just use the placeholder number.</p>`,
    },
    '2.12.1': {
        image: '/assets/illustrations/coach-facepalming.png',
        imageAlt: 'Coach facepalming',
        title: 'Estimate the 4th Step',
        text: ({ overflow }) => `<p>That's a pretty {direction} estimate. ${
            overflow
                ? "If someone doesn't look at the menu, they won't place an order. So this must be lower than the number of people looking at the menu."
                : ''
        }</p>
                                <p>From my experience and your prior estimate, the people placing orders will be somewhere between {minValue} and {maxValue}.</p>
                                <p><b>Please pick a number in that range.</b></p>`,
    },
    '2.13': {
        image: '/assets/illustrations/coach-crystal-ball.png',
        imageAlt: 'Coach with crystal ball',
        title: 'Estimate the Last Step',
        text: `<p>Each person will place an order that costs around \${minConversion} to \${maxConversion}. I think your average revenue could be anywhere from \${minValue} to \${maxValue}.</p>
                <p><b>Estimate the last step.</b></p>
                <p><u>Hint:</u> You can just use the placeholder number.</p>`,
    },
    '2.13.1': {
        image: '/assets/illustrations/coach-facepalming.png',
        imageAlt: 'Coach facepalming',
        title: 'Estimate the Last Step',
        text: `<p>That's a pretty {direction} estimate.</p>
                <p>From my experience helping other food trucks, you'll get around \${minConversion} to \${maxConversion} per order. So from those orders you should get between \${minValue}-{maxValue} in revenue per day.</p>
                <p><b>Please pick a number in that range.</b></p>`,
    },
    '2.14': {
        image: '/assets/illustrations/coach-experimenting.png',
        imageAlt: 'Coach in lab coat running an experiment',
        title: 'Test the Prediction',
        text: `<p>Now that we have a new prediction based on a better understanding of our customers, we need to test it.</p>
                <p>But we don't need to open up for a whole month. Since we made our prediction on a per day basis, we can test much faster, with even one day of data.</p>
                <p><b>However, let's open up for just one week to give us enough data to see how accurate our <u>Prediction</u> is.</b></p>`,
        button1: {
            labels: [
                "Let's Serve Up Success!",
                "Let's Fire Up the Flavor!",
                'Time to Taste Triumph!',
                'Unleash the Feast!',
                'Time to Spice Up Success!',
            ],
            lesson: '2.15',
        },
    },
    '2.15': {
        texts: truckOpenTexts,
    },
    '2.16': {
        image: '/assets/illustrations/coach-happy.png',
        imageAlt: 'Coach happy',
        title: 'Improved Prediction',
        text: `<p><b>Congratulations, your prediction is already much better!</b></p>
                <p>Your initial prediction was within {initialAccuracy}% of your actual revenue. Your revised prediction is within {stepsAccuracy}% of actual revenue.</p>
                <p>But we've still got three weeks of funds left - so we can keep improving.</p>`,
        button1: {
            label: 'How does this work in the Real World?',
            lesson: '2.x',
        },
    },
    '2.x': {
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Globe',
        title: 'The Real World',
        text: `<p>A food truck is a simple example, but breaking the process down step by step makes it much easier to understand. Each step is a potential area for improvement and is more measurable.</p>
                <p>If you want to understand your business:</p>
                <ul>
                    <li>Map it in small steps.</li>
                    <li>Make each step measurable.</li>
                </ul>
                <p>Smaller steps will improve your predictions.</p>`,
        button1: {
            label: 'How can I improve my predictions even more?',
            lesson: '3',
        },
    },
    '3': {
        heading: 'Level 3',
        title: 'Observe and Update',
        text: 'Measure real world data against your model to understand where you are right and where you are wrong.',
        button: 'Play',
        image: '/assets/illustrations/food-truck-observation.png',
        imageAlt: 'Person with clipboard observing the food truck with binoculars',
    },
    '3.1': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Zoom In',
        text: `<p>Since we have a complete user journey, we can zoom in on a single step.</p>
                <p>Let's check your prediction of the number of people walking by on the street compared to reality.</p>
                <p><b>Click on the magnifying glass icon on the first step to see the data.</b></p>`,
    },
    '3.2': {
        image: '/assets/illustrations/coach-levers.png',
        imageAlt: 'Coach operating two levers',
        title: 'Adjust Prediction',
        text: ({ correct }) =>
            correct
                ? `<p>Ok that's a pretty good estimate.</p>
                <p><b>Zoom out when you are done.</b></p>`
                : `<p>Looking at the data from the first step, we can see that your prediction is a little {direction}. So let's adjust it to match the data as closely as possible.</p>
                <p>You can either change the number in the funnel OR you can drag the prediction line in the graph.</p>
                <p><b>Adjust the prediction and zoom out when you are done.</b></p>`,
    },
    '3.2.1': {
        image: '/assets/illustrations/coach-facepalming.png',
        imageAlt: 'Coach facepalming',
        title: 'Adjust Prediction',
        text: ({ correct }) =>
            correct
                ? `<p>Ok that's a pretty good estimate.</p>`
                : `<p>Ok that's a pretty {direction} estimate.</p>
                <p><b>Adjust the estimate to be close to the average.</b></p>`,
    },
    '3.3': {
        image: '/assets/illustrations/coach-happy.png',
        imageAlt: 'Coach happy',
        title: 'Updated Prediction',
        text: `<p>Each time you adjust an individual step, the overall funnel will update. But you'll need to update each step.</p>
                <p>Next, we could adjust step 2 directly. But instead, look at the conversion rates between each step. It will be easier to measure and update.</p>`,
        button1: {
            label: 'Show me the conversion rates.',
            lesson: '3.4',
        },
    },
    '3.4': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Conversion Rates',
        text: `<p>Great! Now we can see the conversion rate, which makes it a lot easier to estimate revenue.</p>
                <p>For example, I can think about what % of people are hungry when they walk past my food truck at lunch time, but it's harder to think about the total number of people per day who will be hungry.</p>
                <p><b>Let's zoom in on the Step 1 &rightarrow; 2 conversion rate to see the data we gathered.</b></p>`,
        button1: {
            action: 'fill',
            color: 'secondary',
            label: 'I know about conversion rates. Please update these for me.',
        },
    },
    '3.5': {
        image: '/assets/illustrations/coach-thinking.png',
        imageAlt: 'Coach thinking with hand on chin',
        title: 'Step 1 &rightarrow; Step 2 Conversion',
        text: `<p>Now we see the data for the conversion rate compared to our prediction.</p>
                <p>You can see that your prediction is a little high. So adjust your prediction to match the data as closely as possible, just as you did before.</p>
                <p>You can either change the number in the funnel OR you can drag the prediction line in the graph.</p>
                <p><b>Adjust the prediction and zoom out when you are done.</b></p>`,
    },
    '3.6': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Updated Prediction',
        text: `<p>Again we can see that by adjusting your prediction on an individual step, your overall revenue prediction has updated.</p>
                <p>So our revenue prediction getting closer and closer Now it's time to adjust your estimates or conversion rates for the rest of the steps.</p>
                <p><b>Let's zoom in on the Step 2 &rightarrow; 3 conversion rate to see the data we gathered.</b></p>`,
        button2: {
            action: 'fill',
            color: 'secondary',
            label: 'I understand how to update estimates based on data. Please update these for me.',
        },
    },
    '3.7': {
        image: '/assets/illustrations/coach-thinking.png',
        imageAlt: 'Coach thinking with hand on chin',
        title: 'Step 2 &rightarrow; Step 3 Conversion',
        text: `<p>Again, we can see the data for the conversion rate compared to our prediction.</p>
                <p>Adjust your prediction to match the data as closely as possible, just as you did before.</p>
                <p>You can either change the number in the funnel OR you can drag the prediction line in the graph.</p>
                <p><b>Adjust the prediction and zoom out when you are done.</b></p>`,
    },
    '3.8': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Updated Prediction',
        text: `<p>Again, your overall revenue prediction has updated.</p>
                <p>Just a few more steps.</p>
                <p><b>Let's zoom in on the Step 3 &rightarrow; 4 conversion rate to see the data we gathered.</b></p>`,
        button2: {
            action: 'fill',
            color: 'secondary',
            label: 'I understand how to update estimates based on data. Please update these for me.',
        },
    },
    '3.9': {
        image: '/assets/illustrations/coach-thinking.png',
        imageAlt: 'Coach thinking with hand on chin',
        title: 'Step 3 &rightarrow; Step 4 Conversion',
        text: `<p>Again, adjust your prediction to match the data as closely as possible.</p>
                <p>You can either change the number in the funnel OR you can drag the prediction line in the graph.</p>
                <p><b>Adjust the prediction and zoom out when you are done.</b></p>`,
    },
    '3.10': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Updated Prediction',
        text: `<p>Again, your overall revenue prediction has updated.</p>
                <p>Last time! We just need to adjust the amount of revenue per order!</p>
                <p><b>Let's zoom in on the Step 4 &rightarrow; 5 conversion rate to see the data we gathered.</b></p>`,
        button2: {
            action: 'fill',
            color: 'secondary',
            label: 'I understand how to update estimates based on data. Please update these for me.',
        },
    },
    '3.11': {
        image: '/assets/illustrations/coach-thinking.png',
        imageAlt: 'Coach thinking with hand on chin',
        title: 'Step 4 &rightarrow; Step 5 Conversion',
        text: `<p>Again, adjust your prediction to match the data as closely as possible.</p>
                <p>You can either change the number in the funnel OR you can drag the prediction line in the graph.</p>
                <p><b>Adjust the prediction and zoom out when you are done.</b></p>`,
    },
    '3.12': {
        image: '/assets/illustrations/coach-happy.png',
        imageAlt: 'Coach happy',
        title: 'Final Prediction',
        text: `<p>Now when we put those individual estimates together, we get an improved overall prediction for your revenue per day.</p>
                <p>As we can see in the graph below, your new, data-based prediction is \${step5Max}/day.</p>
                <p><b>Let's open for one more week and see if our latest <i>Prediction</i> is more accurate.</b></p>`,
        button1: {
            label: "Let's cook!",
            lesson: '3.13',
        },
    },
    '3.13': {
        texts: truckOpenTexts,
    },
    '3.14': {
        image: '/assets/illustrations/coach-happy.png',
        imageAlt: 'Coach happy',
        title: 'Way better!',
        text: `<p>Your initial prediction was within {initialAccuracy}% of your actual revenue. Your second prediction was within {stepsAccuracy}%.</p>
                <p>By using the data, your latest prediction was <i>mostly</i> within {inliersAccuracy}%.</p>
                <p>Yesterday your revenue was unusually high. That's an Outlier. So we can still improve our predictions.</p>`,
        button1: {
            label: 'Is this how real businesses work?',
            lesson: '3.x',
        },
    },
    '3.x': {
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Globe',
        title: 'The Real World',
        text: `<p>Updating with real data is the best way to make predictions. Organizations that don't measure well, don't perform well.</p>
                <ul>
                    <li>Map the customer journey.</li>
                    <li>Measure each step of that journey.</li>
                    <li>Update predictions based on real data.</li>
                </ul>
                <p>However, even with the best data, there will still be unpredictable events called Outliers. These Outliers may be good (like this one) or they may be bad.</p>
                <p>To deal with these events, we have stop predicting the <i>average</i> and start predicting with <i>ranges</i>.</p>`,
        button1: {
            label: "You're going to try teach me statistics now, aren't you?",
            lesson: '4',
        },
    },
    '4': {
        heading: 'Level 4',
        title: 'Estimate with Ranges',
        text: 'Predict outcomes with ranges to avoid surprises.',
        button: 'Play',
        image: '/assets/illustrations/person-crystal-ball.png',
        imageAlt: 'Person with crystal ball',
    },
    '4.1': {
        image: '/assets/illustrations/coach-range.png',
        imageAlt: 'Coach with range symbol',
        title: 'Estimates with Ranges',
        text: `<p>On the last day you had a <i>lot</i> of revenue. That's called an <i>Outlier</i> - an unexpected, extreme event.</p>
                <p>It's great that we have more revenue, but it threw our accuracy off.</p>
                <p>If we ignore the Outlier, we have an accuracy of {inliersAccuracy}%. But we can't just ignore data we don't like.</p>
                <p>We can improve our accuracy and account for Outliers by making estimates using ranges.</p>`,
        button1: {
            label: 'Ok, like best and worst case?',
            lesson: '4.2',
        },
    },
    '4.2': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Zoom In',
        text: `<p>This is a little more complex than best and worst case scenarios.</p>
                <p>If we use a range, we can show how much Uncertainty there is in our prediction.</p>
                <p>We can see this a bit better with a different kind of graph called a Histogram.</p>
                <p><b>Let's zoom in on the first step to get started.</b></p>`,
        button1: {
            color: 'secondary',
            label: "I know all about Histograms, let's skip to estimating with ranges.",
            lesson: '4.10',
        },
    },
    '4.3': {
        image: '/assets/illustrations/coach-growth-chart.png',
        imageAlt: 'Coach pointing at growth chart',
        title: 'Introducing Histograms',
        text: `<p>So far, we've used a graph that shows results vs. time. That graph is on top.</p>
                <p>Below that, we'll create a histogram, which is a way of representing the <b>frequency</b> of an event.</p>
                <p>This lets us see how often our results are close to our predicted average.</p>`,
        button1: {
            label: "Ok, let's make a histogram.",
            lesson: '4.4',
        },
    },
    '4.4': {
        image: '/assets/illustrations/coach-waving.png',
        imageAlt: 'Coach waving',
        title: 'Count the Frequency',
        text: `<p>On the histogram below, we show the number of people walking by the food truck on the X-axis.</p>
                <p>Then on the Y axis, we count the number of days that the number of people walking by the food truck was between {min} and {max}.</p>
                <p>We can see on the chart above that this only happened {occurrence} in our two-week span. So the frequency of people walking by the food truck in a single day is {frequency}.</p>
                <p>We add this below to start our histogram.</p>`,
        button1: {
            label: "Let's keep counting.",
            lesson: '4.5',
        },
        button2: {
            color: 'secondary',
            label: 'I get it, count all of them.',
            lesson: '4.6',
        },
    },
    '4.5': {
        image: '/assets/illustrations/coach-waving.png',
        imageAlt: 'Coach waving',
        title: 'Keep Counting',
        text: `<p>Again looking at our chart above, we see that there are only {frequency} days when between {min} and {max} people walked by the food truck.</p>
                <p>So the frequency for the {min}-{max} range is {frequency}. Again, we add that to the histogram below.</p>`,
        button1: {
            label: "Let's keep counting.",
            lesson: '4.5.x',
        },
        button2: {
            color: 'secondary',
            label: 'I get it, count all of them.',
            lesson: '4.6',
        },
    },
    '4.5.x': {
        image: '/assets/illustrations/coach-waving.png',
        imageAlt: 'Coach waving',
        title: 'Keep Counting',
        text: `<p>Next, there are {frequency} days when between {min} and {max} people walked by the food truck.</p>
                <p>So the frequency for the {min}-{max} range is {frequency}. Again, we add that to the histogram below.</p>`,
        button1: {
            action: 'next',
            label: 'Ok... keep counting',
        },
        button2: {
            color: 'secondary',
            label: 'I get it, count all of them.',
            lesson: '4.6',
        },
    },
    '4.6': {
        image: '/assets/illustrations/coach-growth-chart.png',
        imageAlt: 'Coach pointing at growth chart',
        title: 'Complete Histogram',
        text: `<p>If we repeat this process for each range, counting the frequency for each result on the X-axis, we get a complete histogram.</p>
                <p>This histogram is taller in the middle the number of days that {min}-{max} people walk by the food truck is the most frequent result.</p>
                <p>Our outliers are rare, which is why they lie on the outside of the histogram.</p>
                <p>The histogram shows us how frequent different results are and helps us spot outliers.</p>`,
        button1: {
            label: 'Uh....this is too many graphs',
            lesson: '4.7',
        },
    },
    '4.7': {
        image: '/assets/illustrations/coach-switch.png',
        imageAlt: 'Coach with switch icon',
        title: 'Toggle Between Graphs',
        text: `<p>Don't worry, you can toggle between these graphs as needed.. But let's look at the histogram for now because it's really useful when estimating in ranges. That's what we're going to do in a second.</p>
                <p><b>Press the button to toggle to the Histogram.</b></p>`,
    },
    '4.8': {
        image: '/assets/illustrations/coach-bell-curve.png',
        imageAlt: 'Coach with bell under bell curve',
        title: 'Bell Curves',
        text: `<p>If everything was predictable and happened the same way every day, we wouldn't have a histogram. It would just be a single column. Instead, we have something that looks like a bell - unsurprisingly called a Bell Curve.</p>
                <p>A bell curve is a way of representing the <i>Probability</i> of any given result.</p>
                <p>Most results will be in the middle of the bell curve, but some will be near the edges - those are the Outliers.</p>
                <p>So when we predict a single number of {predictionInitialMonthly} people per day, we are giving a precise estimate that is <u>almost always wrong</u>.</p>`,
        button1: {
            label: "What's the alternative?",
            lesson: '4.9',
        },
    },
    '4.9': {
        image: '/assets/illustrations/coach-danger-range.png',
        imageAlt: 'Coach with danger range icon',
        title: 'Expressing Uncertainty',
        text: `<p>Sometimes the weather will be good and you'll get a lot of business. Sometimes it will be bad and you'll sell very little food. That's life in a food truck.</p>
                <p>If we give a single number as the prediction, we will be close every time, but definitely be wrong all the time.</p>
                <p>If we express the <i>range</i> of possible outcomes, we are telling the bank that we really understand our business and are ready for the unexpected.</p>`,
        button1: {
            label: 'So how do I predict the range?',
            lesson: '4.10',
        },
    },
    '4.10': {
        image: '/assets/illustrations/coach-range.png',
        imageAlt: 'Coach with range symbol',
        title: 'Estimating with Ranges',
        text: `<p>So let's try updating our estimate with a Range.</p>
                <p>Given the model a range as an input will allow the model to give a range of possible outcomes as an output.</p>
                <p>In other words, a range represents the reality that there is variation from day to day.</p>
                <p>Adjust the min and max of the range until the blue bell curve matches the actual data.</p>
                <p><b>Adjust the min and max by dragging the grey handles or adjusting the values directly.</b></p>
                <p><b>Zoom Out when you are done.</b></p>`,
    },
    '4.10.1': {
        image: '/assets/illustrations/coach-facepalming.png',
        imageAlt: 'Coach facepalming',
        title: 'Estimating with Ranges',
        text: `<p>Ok your ranges are pretty far off.</p>
                <p><b>Adjust the ranges so the bell curves match as closely as possible.</b></p>`,
    },
    '4.11': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Updated Prediction',
        text: `<p>We can now see our overall prediction is no longer a single line, it's a range.</p>
                <p>Let's keep going and make a <u>Range Prediction</u> for our entire funnel.</p>
                <p><b>Let's zoom in on the Step 1 &rightarrow; 2 conversion rate.</b></p>`,
        button1: {
            action: 'fill',
            color: 'secondary',
            label: 'Fill in the range predictions for all of the steps for me.',
        },
    },
    '4.12': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Step 1 &rightarrow; Step 2 Conversion',
        text: `<p>Again, instead of predicting a single number, adjust the min and max of the range until the blue bell curve closely matches the data.</p>
                <p><b>Adjust the min and max by dragging the grey handles or adjusting the values directly.</b></p>
                <p><b>Zoom Out when you are done.</b></p>`,
    },
    '4.12.1': {
        image: '/assets/illustrations/coach-facepalming.png',
        imageAlt: 'Coach facepalming',
        title: 'Step 1 &rightarrow; Step 2 Conversion',
        text: `<p>Ok your ranges are pretty far off.</p>
                <p><b>Adjust the ranges so the bell curves match as closely as possible.</b></p>`,
    },
    '4.13': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Updated Prediction',
        text: `<p>Again we can see that by adjusting your prediction on an individual step, your overall revenue prediction has updated.</p>
                <p>So our revenue prediction getting closer and closer Now it's time to adjust your estimates or conversion rates for the rest of the steps.</p>
                <p><b>Let's zoom in on the Step 2 &rightarrow; 3 conversion rate to see the data we gathered.</b></p>`,
        button1: {
            action: 'fill',
            color: 'secondary',
            label: 'I understand how to update estimates based on data. Please update these for me.',
        },
    },
    '4.14': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Step 2 &rightarrow; Step 3 Conversion',
        text: `<p>Again, we can see the data for the conversion rate compared to our prediction.</p>
                <p><b>Adjust the min and max by dragging the grey handles or adjusting the values directly.</b></p>
                <p><b>Zoom Out when you are done.</b></p>`,
    },
    '4.14.1': {
        image: '/assets/illustrations/coach-facepalming.png',
        imageAlt: 'Coach facepalming',
        title: 'Step 2 &rightarrow; Step 3 Conversion',
        text: `<p>Ok your ranges are pretty far off.</p>
                <p><b>Adjust the ranges so the bell curves match as closely as possible.</b></p>`,
    },
    '4.15': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Updated Prediction',
        text: `<p>Again, your overall revenue prediction has updated.</p>
                <p>Just a few more steps.</p>
                <p><b>Let's zoom in on the Step 3 &rightarrow; 4 conversion rate to see the data we gathered.</b></p>`,
        button1: {
            action: 'fill',
            color: 'secondary',
            label: 'I understand how to update estimates based on data. Please update these for me.',
        },
    },
    '4.16': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Step 3 &rightarrow; Step 4 Conversion',
        text: `<p>Again, adjust your prediction to match the data as closely as possible.</p>
                <p><b>Adjust the min and max by dragging the grey handles or adjusting the values directly.</p>
                <p><b>Zoom Out when you are done.</b></p>`,
    },
    '4.16.1': {
        image: '/assets/illustrations/coach-facepalming.png',
        imageAlt: 'Coach facepalming',
        title: 'Step 3 &rightarrow; Step 4 Conversion',
        text: `<p>Ok your ranges are pretty far off.</p>
                <p><b>Adjust the ranges so the bell curves match as closely as possible.</b></p>`,
    },
    '4.17': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Updated Prediction',
        text: `<p>Again, your overall revenue prediction has updated.</p>
                <p>Last time! We just need to adjust the amount of revenue per order!</p>
                <p><b>Let's zoom in on the Step 4 &rightarrow; 5 conversion rate to see the data we gathered.</b></p>`,
        button1: {
            action: 'fill',
            color: 'secondary',
            label: 'I understand how to update estimates based on data. Please update these for me.',
        },
    },
    '4.18': {
        image: '/assets/illustrations/coach-magnifying-glass.png',
        imageAlt: 'Coach with magnifying glass',
        title: 'Step 4 &rightarrow; Step 5 Conversion',
        text: `<p>Again, adjust your prediction to match the data as closely as possible.</p>
                <p><b>Adjust the min and max by dragging the grey handles or adjusting the values directly.</p>
                <p><b>Zoom Out when you are done.</b></p>`,
    },
    '4.18.1': {
        image: '/assets/illustrations/coach-facepalming.png',
        imageAlt: 'Coach facepalming',
        title: 'Step 4 &rightarrow; Step 5 Conversion',
        text: `<p>Ok your ranges are pretty far off.</p>
                <p><b>Adjust the ranges so the bell curves match as closely as possible.</b></p>`,
    },
    '4.19': {
        image: '/assets/illustrations/coach-range.png',
        imageAlt: 'Coach with range symbol',
        title: 'Final Estimate with Ranges',
        text: `<p>Let's open the food truck for 2 more weeks and see if our new prediction (based on range) is more accurate than our initial prediction (based on a number).</p>`,
        button1: {
            labels: truckOpenLabels,
            lesson: '4.20',
        },
    },
    '4.20': {
        texts: truckOpenTexts,
    },
    '4.21': {
        image: '/assets/illustrations/banker-happy.png',
        imageAlt: 'Banker happy',
        title: 'Wow!',
        text: `<p>Your coach told me - your predictions are spot on!</p>
                <p>By estimating with a range you are less precise, but more accurate. Your actual revenue is almost always within your predicted range.</p>
                <p>This accurately conveys the degree of uncertainty in your business and gives me a lot of confidence that you know what you are talking about and will be able to pay back your loans.</p>
                <p>I'm going to approve 6 more months of funding so we can see how your business grows!</p>`,
        button1: {
            label: 'Is this how real businesses work?',
            lesson: '4.x',
        },
    },
    '4.x': {
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Globe',
        title: 'The Real World',
        text: `<p>Using ranges for predictions is less precise, but more realistic. It tells you how much uncertainty there is and helps you anticipate Outliers.</p>
                <p>In this simulation an Outlier is no big deal, but in the real world a food truck might not have enough ingredients to feed all those people and would have lost out on extra business.</p>
                <ul>
                    <li>Every innovation has uncertainty.</li>
                    <li>Ranges express uncertainty.</li>
                    <li>A histogram helps visualize uncertainty.</li>
                </ul>`,
        button1: {
            label: 'How can I represent growth in my model?',
            lesson: '5',
        },
    },
    '5': {
        heading: 'Level 5',
        title: 'Mapping Growth',
        text: 'Understand how to visualize and measure growth loops in your business.',
        button: 'Play',
        image: '/assets/illustrations/person-growth-chart.png',
        imageAlt: 'Person with growth chart',
    },
    '5.1': {
        image: '/assets/illustrations/banker-celebrating.png',
        imageAlt: 'Banker celebrating',
        title: 'Congratulations!',
        text: `<p>The bank gave you funding for six months so we can just open for business as see how things are going.</p>`,
        button1: {
            labels: truckOpenLabels,
            lesson: '5.2',
        },
    },
    '5.2': {
        texts: truckOpenTexts,
    },
    '5.3': {
        image: '/assets/illustrations/coach-growth-chart.png',
        imageAlt: 'Coach pointing at growth chart',
        title: 'Huh!',
        text: `<p>Well, good news, you're doing better than expected - <u>consistently</u>!</p>
                <p>In fact, it looks like your business is growing over time.</p>
                <p>The bad news is that our model isn't working anymore.</p>`,
        button1: {
            label: 'So what? Growth is good right?',
            lesson: '5.4',
        },
    },
    '5.4': {
        image: '/assets/illustrations/coach-shrugging.png',
        imageAlt: 'Coach shrugging',
        title: 'Predicting Growth',
        text: `<p>Growth is great, but we need to make sure we order enough ingredients to serve all these extra customers.</p>
                <p>The bank will also want an accurate growth prediction if you want to expand to more trucks.</p>
                <p>So we need to represent growth in our model with either Retention or Referral Loops.</p>
                <p>Let's start with Retention.</p>`,
        button1: {
            label: 'How do we add retention?',
            lesson: '5.5',
        },
    },
    '5.5': {
        image: '/assets/illustrations/coach-retention-loop.png',
        imageAlt: 'Coach showing retention loop',
        title: 'Start Retention Loop',
        text: `<p>Right now, your funnel is only representing *new* customers. Every day starts new.</p>
                <p>But in reality, your satisfied customers come back. This is why your growth prediction was off.</p>
                <p>So we can represent this by drawing a loop from an order placed (and enjoyed!)</p>
                <p><b>Click the + icon to add a retention loop.</b></p>`,
    },
    '5.6': {
        image: '/assets/illustrations/coach-retention-loop.png',
        imageAlt: 'Coach showing retention loop',
        title: 'Close the Loop',
        text: `<p>A satisfied customer could come back to any point in our funnel, but let's assume that a certain amount of satisfied customers will come back <u>and look at the menu</u>.</p>
                <p>Some of them will look at the menu and place an order and some will decide they are not in the mood for {foodType} and eat somewhere else.</p>
                <p><b>Click to connect the loop to the previous step.</b></p>`,
    },
    '5.7': {
        image: '/assets/illustrations/coach-thinking-clipboard.png',
        imageAlt: 'Coach thinking with hand on chin',
        title: 'Estimate Retention',
        text: `<p>Now that we've got our loop, we need to estimate what percentage of people who place orders will actively return to the food truck the next day.</p>
                <p><b>Let's zoom in on the retention loop.</b></p>`,
    },
    '5.8': {
        image: '/assets/illustrations/coach-calendar.png',
        imageAlt: 'Coach with calendar',
        title: 'Forecasting Growth',
        text: `<p>In reality, someone may come next day or come back a week later. So this estimate is a simplification.</p>
                <p>We have some data on returning customers before, so try to make the bell curve match as usual.</p>
                <p><b>Adjust the min and max by dragging the grey handles or adjusting the values directly.</b></p>
                <p><b>Zoom Out when you are done.</b></p>`,
    },
    '5.8.1': {
        image: '/assets/illustrations/coach-facepalming.png',
        imageAlt: 'Coach facepalming',
        title: 'Estimating with Ranges',
        text: `<p>Ok your ranges are pretty far off.</p>
                <p><b>Adjust the ranges so the data falls within the min and max.</b></p>
                <p><b>Zoom Out when you are done.</b></p>`,
    },
    '5.9': {
        image: '/assets/illustrations/coach-danger-range.png',
        imageAlt: 'Coach with danger range icon',
        title: 'Too Much Uncertainty',
        text: `<p>Your estimation fits the data we have, but there is a really big difference between the best and worst case here. The bank won't like that much uncertainty.</p>
                <p>It's very rare for everything to go perfectly. It's equally rare for everything to go perfectly wrong.</p>
                <p>So we can use a technique called a <i>Monte Carlo simulation</i> to figure out the most likely outcome.</p>`,
        button1: {
            label: 'Monte Carlo? Is that like gambling?',
            lesson: '5.10',
        },
        button2: {
            color: 'secondary',
            label: 'I know Monte Carlo simulations - show me the graph.',
            lesson: '5.15',
        },
    },
    '5.10': {
        image: '/assets/illustrations/coach-dice.png',
        imageAlt: 'Coach with dice',
        title: 'Monte Carlo Simulation',
        text: `<p>Monte Carlo simulation is named after gambling because it deals with random numbers.</p>
                <p>We're going to use a computer to simulate your food truck based on your estimates with random numbers. We'll do this a few <i>thousand</i> times and then see what the most likely outcome is.</p>
                <p>A Monte Carlo simulation is a way to predict the likelihood of an outcome by creating random numbers between your best case and worst case estimates thousands of times.</p>`,
        button1: {
            label: 'Ok, I get it, just show me the result.',
            lesson: '5.12',
        },
        button2: {
            color: 'secondary',
            label: 'Is this related to the Bell Curve?',
            lesson: '5.11',
        },
    },
    '5.11': {
        image: '/assets/illustrations/coach-bell-curve.png',
        imageAlt: 'Coach with bell under bell curve',
        title: 'Bell Curve',
        text: `<p>It's more likely that the number of people walking by your food truck is in the middle of your {step1Min}-{step1Max} range than the extreme edges. So we're going to use a <i>Normal Distribution for our random numbers</i>.</p>
                <p>That's a fancy word for the bell curve in your histogram. So we'll make random numbers for each of your estimates based on the bell curve.</p>
                <p>(There are other distributions - but this is good enough for now.)</p>`,
        button1: {
            label: 'Ok, show me the result.',
            lesson: '5.12',
        },
    },
    '5.12': {
        image: '/assets/illustrations/coach-hurricane-chart.png',
        imageAlt: 'Coach with hurricane chart',
        title: 'Hurricane Graph',
        text: `<p>We can represent the results of a Monte Carlo simulation with a Hurricane Graph.</p>
                <p>This is Kromatic's way to visually represent uncertainty by showing the potential path of our revenue or impact over time. It's the same way a weather forecaster represents the path of a hurricane over time.</p>
                <p>Each color shows the likelihood of the real result landing in that area.</p>
                <p><green>GREEN</green> - Most likely outcome</p>`,
        button1: {
            label: 'Next is yellow I suppose?',
            lesson: '5.13',
        },
        button2: {
            color: 'secondary',
            label: 'Show me the whole graph.',
            lesson: '5.15',
        },
    },
    '5.13': {
        image: '/assets/illustrations/coach-hurricane-chart.png',
        imageAlt: 'Coach with hurricane chart',
        title: 'Hurricane Graph',
        text: `<p>Each color shows the likelihood of the real result landing in that area.</p>
                <p>
                    <green>GREEN</green> - Most likely outcome<br/>
                    <yellow>YELLOW</yellow> - Less likely
               </p>`,
        button1: {
            label: 'Then orange?',
            lesson: '5.14',
        },
        button2: {
            color: 'secondary',
            label: 'Show me the whole graph.',
            lesson: '5.15',
        },
    },
    '5.14': {
        image: '/assets/illustrations/coach-hurricane-chart.png',
        imageAlt: 'Coach with hurricane chart',
        title: 'Hurricane Graph',
        text: `<p>Each color shows the likelihood of the real result landing in that area.</p>
                <p>
                    <green>GREEN</green> - Most likely outcome<br/>
                    <yellow>YELLOW</yellow> - Less likely<br/>
                    <orange>ORANGE</orange> - Unlikely
               </p>`,
        button1: {
            label: 'And red of course?',
            lesson: '5.15',
        },
    },
    '5.15': {
        image: '/assets/illustrations/coach-hurricane-chart.png',
        imageAlt: 'Coach with hurricane chart',
        title: 'Hurricane Graph',
        text: `<p>Each color shows the likelihood of the real result landing in that area.</p>
                <p>
                    <green>GREEN</green> - <b>Most likely outcome</b><br/>
                    <yellow>YELLOW</yellow> - <b>Less likely</b><br/>
                    <orange>ORANGE</orange> - <b>Unlikely</b><br/>
                    <red>RED</red> - <b>Highly unlikely</b>
               </p>`,
        button1: {
            label: "Ok...let's see if my prediction is correct",
            lesson: '5.16',
        },
    },
    '5.16': {
        texts: truckOpenTexts,
    },
    '5.17': {
        image: '/assets/illustrations/coach-and-banker-celebrating.png',
        imageAlt: 'Coach and banker celebrating with confetti',
        title: 'Fantastic!',
        text: `<p>Congratulations from both of us! You're accurately predicting your revenue including growth.</p>
                <p>You're in a great position to expand your business by including marketing tactics like coupons to create a Referral loop. That will grow your business even more!</p>
                <p>In fact, you don't even really need a bank loan anymore because you can fund your own expansion!</p>`,
        button1: {
            label: 'How does this work in the real world?',
            lesson: '5.x',
        },
    },
    '5.x': {
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Globe',
        title: 'The Real World',
        text: `<p>Yup... that's how it works in the real world.</p>
                <p>Traditional financial modeling gives you a precise prediction that is <u>always wrong</u>.</p>
                <p>Innovation Accounting is deliberately imprecise because innovation is inherently uncertain. So we use probability to make predictions and update it with real data.</p>
                <ul>
                    <li>Every innovation has uncertainty.</li>
                    <li>Ranges express uncertainty.</li>
                    <li>A histogram helps visualize uncertainty.</li>
                    <li>A hurricane graph visualizes possible outcomes.</li>
                </ul>
                <p><u>Realistic predictions can help you grow your business.</u></p>`,
        button1: {
            label: "What's next?",
            link: '/survey',
        },
        button2: {
            color: 'secondary',
            label: 'Understand the model? Read The Real Startup Book — free online',
            link: RSB_URL,
        },
    },
};

export const lessonKeys = Object.keys(lessons).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
);
export const lessonParents = Array.from(new Set(lessonKeys.map(key => parseInt(key))));
